import React, { useCallback, useMemo } from 'react';

import useLeanFacetQuery from '../hooks/useLeanFacetQuery';
import { isCioSizeFacet, isProductsV2SizeFacet } from '../utils/filters';

/**
 * FilterAvailabilityContext determines which filter options remain available
 * given the current staged selections, enabling disabled states without
 * fetching full product data. See docs/grid-provider-architecture.md
 * "Staged Filters & Disabled Option States" for the full design.
 */

export interface FilterAvailabilityContextValue {
  /** Whether the dynamic lean query is settling (debounce or in-flight) */
  isDynamicQueryLoading: boolean;
  /** Check if a specific facet option should be disabled (not available) */
  isFacetOptionDisabled: (facetName: string, optionValue: string) => boolean;
  /** Base facets from unfiltered lean query (full set of options for rendering) */
  baseFacets: any[] | null;
  /** Total result count from the base (unfiltered) lean query */
  baseTotalNumResults: number | null;
  /** Total result count from the dynamic lean query */
  dynamicTotalNumResults: number | null;
}

export const FilterAvailabilityContext = React.createContext<
  FilterAvailabilityContextValue | undefined
>(undefined);

export const useFilterAvailability = (): FilterAvailabilityContextValue => {
  const context = React.useContext(FilterAvailabilityContext);

  // Safe defaults when provider is absent (Products V2 grids, feature flag off)
  if (!context) {
    return {
      isDynamicQueryLoading: false,
      isFacetOptionDisabled: () => false,
      baseFacets: null,
      baseTotalNumResults: null,
      dynamicTotalNumResults: null,
    };
  }

  return context;
};

/**
 * Hook containing all filter availability logic. Consumed by the provider
 * to keep the provider file thin.
 *
 * Receives staged filter data as arguments (rather than calling useStagedFilters)
 * because PendingFiltersProvider calls this hook in its body — before
 * StagedFiltersContext.Provider is mounted in the tree.
 */
export const useFilterAvailabilityState = ({
  stagedFilters,
  stagedFiltersAsUrlParams,
}: {
  stagedFilters: Record<string, string[]>;
  stagedFiltersAsUrlParams: Record<string, string[]>;
}) => {
  const {
    baseFacets,
    baseTotalNumResults,
    dynamicFacets,
    dynamicTotalNumResults,
    isDynamicQueryLoading,
  } = useLeanFacetQuery({
    stagedFilters: stagedFiltersAsUrlParams,
  });

  // Build a lookup Set from the lean query response. dynamicFacets is frozen
  // through loading by the ref in useLeanFacetQuery, so this Set holds steady
  // during debounce + fetch without any extra logic here.
  const dynamicOptionsSet = useMemo(() => {
    if (!dynamicFacets) {
      return null;
    }

    const set = new Set<string>();
    for (const facet of dynamicFacets) {
      for (const option of facet.options || []) {
        set.add(`${facet.name}::${option.value}`);
      }
    }
    return set;
  }, [dynamicFacets]);

  const isFacetOptionDisabled = useCallback(
    (facetName: string, optionValue: string) => {
      // No fresh results yet — nothing disabled
      if (!dynamicOptionsSet) {
        return false;
      }

      // Never disable size facets — selecting a size can cause sibling size
      // options to disappear from the CIO response, which is a known BE issue.
      // Workaround until resolved: https://techstyle-prod.atlassian.net/browse/FLCE-3157
      if (isCioSizeFacet(facetName) || isProductsV2SizeFacet(facetName)) {
        return false;
      }

      // Never disable a currently staged/selected option (user can always deselect)
      const stagedValues = stagedFilters[facetName] || [];
      if (stagedValues.some((v) => String(v) === String(optionValue))) {
        return false;
      }

      // Disabled if NOT in the last fresh lean query response
      return !dynamicOptionsSet.has(`${facetName}::${optionValue}`);
    },
    [dynamicOptionsSet, stagedFilters]
  );

  return useMemo(
    () => ({
      isDynamicQueryLoading,
      isFacetOptionDisabled,
      baseFacets,
      baseTotalNumResults,
      dynamicTotalNumResults,
    }),
    [
      isDynamicQueryLoading,
      isFacetOptionDisabled,
      baseFacets,
      baseTotalNumResults,
      dynamicTotalNumResults,
    ]
  );
};
